<template>
    <!-- Grupos de links do Mapa do Site, com busca. Usado no painel lateral e na página /mapa-do-site. -->
    <div>
        <label :for="idBusca" class="sr-only">Buscar no mapa do site</label>
        <div class="relative">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            <input :id="idBusca" ref="campoBusca" v-model="busca" type="search" autocomplete="off" placeholder="Buscar página (ex.: ouvidoria, saúde)"
                class="w-full h-11 pl-9 pr-3 text-sm bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-admin-primary/20 focus:border-admin-primary" />
        </div>
        <p v-if="busca.trim()" class="mt-2 text-xs text-gray-600" aria-live="polite">
            {{ totalEncontrado }} {{ totalEncontrado === 1 ? 'página encontrada' : 'páginas encontradas' }}
        </p>

        <div class="mt-4" :class="colunas ? 'grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3' : 'space-y-3'">
            <section v-for="grupo in gruposVisiveis" :key="grupo.titulo" :aria-labelledby="idDoGrupo(grupo)">
                <h3 class="m-0">
                    <button type="button" :id="idDoGrupo(grupo)"
                        class="w-full flex items-center justify-between gap-2 py-2 text-left text-sm font-bold uppercase tracking-wide text-admin-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary/40 rounded"
                        :aria-expanded="estaAberto(grupo)" :aria-controls="`${idDoGrupo(grupo)}_links`" @click="alternar(grupo)">
                        {{ grupo.titulo }}
                        <ChevronDown class="w-4 h-4 flex-shrink-0 transition-transform motion-reduce:transition-none" :class="{ '-rotate-90': !estaAberto(grupo) }" aria-hidden="true" />
                    </button>
                </h3>
                <ul v-show="estaAberto(grupo)" :id="`${idDoGrupo(grupo)}_links`" class="border-l-2 border-gray-200 pl-3 space-y-0.5">
                    <li v-for="link in grupo.links" :key="link.url">
                        <a :href="link.url" v-bind="link.externo ? { target: '_blank', rel: 'noopener noreferrer' } : {}"
                            class="flex items-center gap-1.5 py-1.5 text-sm text-gray-700 hover:text-admin-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-admin-primary/40 rounded">
                            {{ link.titulo }}
                            <template v-if="link.externo">
                                <ExternalLink class="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                                <span class="sr-only">(abre em nova aba)</span>
                            </template>
                        </a>
                    </li>
                </ul>
            </section>
        </div>

        <p v-if="!gruposVisiveis.length" class="py-8 text-center text-sm text-gray-600">
            Nenhuma página encontrada para “{{ busca.trim() }}”.
        </p>
    </div>
</template>

<script setup>
import { computed, ref, useId } from 'vue'
import { ChevronDown, ExternalLink, Search } from 'lucide-vue-next'
import { combinaComBusca } from '@/Utils/busca'

const props = defineProps({
    // [{ titulo, links: [{ titulo, url, externo }] }]
    grupos: { type: Array, required: true },
    // Página completa: grupos em colunas; painel lateral: um embaixo do outro.
    colunas: { type: Boolean, default: false },
})

const idBase = useId()
const idBusca = `${idBase}_busca`
const campoBusca = ref(null)
const busca = ref('')
const fechados = ref(new Set())

const idDoGrupo = (grupo) => `${idBase}_${props.grupos.indexOf(grupo)}`

// Na busca, mostra só os links que combinam (o título do grupo também conta).
const gruposVisiveis = computed(() => {
    if (!busca.value.trim()) return props.grupos
    return props.grupos
        .map(grupo => combinaComBusca(grupo.titulo, busca.value)
            ? grupo
            : { ...grupo, links: grupo.links.filter(link => combinaComBusca(link.titulo, busca.value)) })
        .filter(grupo => grupo.links.length)
})

const totalEncontrado = computed(() => gruposVisiveis.value.reduce((total, grupo) => total + grupo.links.length, 0))

// Todos abertos por padrão; durante a busca, os resultados ficam sempre visíveis.
const estaAberto = (grupo) => Boolean(busca.value.trim()) || !fechados.value.has(grupo.titulo)

function alternar(grupo) {
    const novos = new Set(fechados.value)
    if (novos.has(grupo.titulo)) novos.delete(grupo.titulo)
    else novos.add(grupo.titulo)
    fechados.value = novos
}

defineExpose({ focarBusca: () => campoBusca.value?.focus() })
</script>
